<template>
  <div style="min-height: 400px; height: 400px;">
    <div v-if="loading">
      <loading-container />
    </div>
    <app-echart-scatter
      v-else
      ref="chart"
      :key="chartKey"
      :option-data="chartData"
      style="height: 100%; width: 100%;"
    />
  </div>
</template>

<script>
import AppEchartScatter from '@core/components/charts/echart/AppEchartScatter.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import renderChartToBase64 from './offscreenRender'

export default {
  components: {
    AppEchartScatter,
    LoadingContainer,
  },
  props: {
    figureThree: {
      type: Object,
      default: () => ({
        datapoints: {},
        thisproductdatapoint: {},
      }),
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      loading: true,
      chartKey: 0,
      chartData: {
        legend: {},
        grid: {},
        xAxis: {},
        yAxis: {},
        series: [],
      },
      cachedImage: null,
    }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
  },
  watch: {
    figureThree: {
      handler(data) {
        if (data && data.datapoints) {
          this.processComparisonData(data)
        }
      },
      immediate: true,
      deep: true,
    },
    skin() {
      if (this.figureThree && this.figureThree.datapoints) {
        this.processComparisonData(this.figureThree)
        this.chartKey += 1
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    processComparisonData(data) {
      const datapointsArray = Object.values(data.datapoints)

      if (datapointsArray.length === 0) {
        this.loading = false
        return
      }

      // Convert product universe data to ECharts format [x, y, label]
      const productUniverseData = datapointsArray.map(dp => [
        parseFloat(dp.datapoint[0]),
        parseFloat(dp.datapoint[1]),
        dp.datalabel,
      ])

      // Convert current product data to ECharts format [x, y, label]
      const dp = data.thisproductdatapoint && data.thisproductdatapoint.datapoint
      const thisProductX = dp ? parseFloat(dp[0]) : NaN
      const thisProductY = dp ? parseFloat(dp[1]) : NaN
      const thisProductData = (!Number.isNaN(thisProductX) && !Number.isNaN(thisProductY)) ? [[
        thisProductX,
        thisProductY,
        data.thisproductdatapoint.datalabel,
      ]] : []

      this.chartData = {
        legend: {
          show: false,
        },
        grid: {
          left: 60,
          right: 20,
          bottom: 60,
          top: 20,
          containLabel: true,
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            snap: false,
            crossStyle: {
              color: '#999',
            },
          },
          formatter: params => {
            if (params && params.length > 0) {
              const pointData = params[0].data
              const label = pointData[2] || ''
              const riskmap = Number(pointData[0]).toFixed(2)
              const score = Number(pointData[1]).toFixed(2)
              return `${label}<br/>Riskmap: ${riskmap}, Overall score: ${score}`
            }
            return ''
          },
        },
        xAxis: {
          type: 'value',
          name: 'Riskmap (/10)',
          nameLocation: 'middle',
          nameGap: 35,
          min: 0,
          max: 10,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          scale: true,
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Overall score (/10)',
          nameLocation: 'middle',
          nameGap: 50,
          min: 0,
          max: 10,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          scale: true,
          splitLine: {
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: 'Product Universe',
            type: 'scatter',
            data: productUniverseData,
            symbolSize: 8,
            itemStyle: {
              color: 'lightblue',
            },
            cursor: 'pointer',
            z: 1,
          },
          {
            name: 'This Product',
            type: 'scatter',
            data: thisProductData,
            symbolSize: 14,
            symbol: 'diamond',
            itemStyle: {
              color: 'red',
            },
            z: 2,
          },
        ],
      }
      this.cachedImage = renderChartToBase64(this.chartData)
      this.loading = false
      this.$nextTick(this.bindChartClick)
    },
    resizeChart() {
      if (this.$refs.chart) {
        this.$refs.chart.resize()
      }
    },
    getDataURL() {
      return this.cachedImage || null
    },
    bindChartClick() {
      const chartInstance = this.$refs.chart && this.$refs.chart.getChartInstance && this.$refs.chart.getChartInstance()
      if (chartInstance) {
        chartInstance.on('click', params => {
          if (params.seriesName === 'Product Universe' && params.data) {
            const label = params.data[2] || ''
            const match = label.match(/(\d+)/)
            if (match) {
              this.$router.push({ name: 'product-report', params: { reportNumber: match[1] } })
            }
          }
        })
      }
    },
  },
}
</script>
